import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { configure, currentRefinements } from "instantsearch.js/es/widgets";
import algoliasearch, { type SearchIndex } from "algoliasearch";
import instantsearch from "instantsearch.js";
import type {
  GlobalContent,
  ProductSettings,
  ContentCardItems,
} from "@wbly/data-storefront";
import type { Marketplace } from "@wbly/common";
import { useStore } from "@nanostores/react";
import type { InfiniteHitsRenderState } from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import connectInfiniteHits from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import connectSortBy, {
  type SortByRenderState,
} from "instantsearch.js/es/connectors/sort-by/connectSortBy";
import { formatAlgoliaHit, ProductCard } from "../ProductCard";
import { ContentCard, type ContentCardProps } from "../ContentCard/ContentCard";
import {
  $filters,
  $isFilterBarLoaded,
  setSortOptions,
  setSelectedSort,
  setResultsCount,
  setSearchInstance,
  setPagination,
} from "../../pages/[locale]/personalized-books/stores/filters.store";
import {
  filterOptions,
  checkReplicaIndices,
  isSpecialPageSegment,
} from "../../util";
import { CopyCodeButton } from "../ContentCard/CopyCodeButton";
import { LoadMore } from "./components/LoadMore";
import {
  generateFacetRefinement,
  type Filter,
} from "./widgets/generateFacetRefinement";
import styles from "./components/ActionBar.module.css";
import { createUniqueRoot, renderWithExtraArguments } from "./helper";

const { chipContainer, chipItemContainer, chipLabel, chipDelete, chip } =
  styles;

export type SortingList = {
  sortingLabel: string;
  sortingAttribute: string;
};

export type SortSettings = {
  sortTitleTranslation: string;
  sortingList: SortingList[];
};

export type InstantSearchProps = {
  productSettings: ProductSettings;
  marketplace: Marketplace;
  apiKey: string;
  appID: string;
  filters: Filter[] | null;
  sortSettings: SortSettings | null;
  listingGlobalContent: GlobalContent["productListing"];
  contentCardItems: ContentCardItems[] | null;
  taxonQuery?: string;
  showSortBy?: boolean;
  lastSegment?: string;
  totalProductsLimit?: number;
};

const InfiniteHits = ({ hits, sendEvent, marketplace, productSettings }) => {
  useEffect(() => {
    if (!hits?.length) return;

    const productHits = hits.filter((hit) => !hit.isMarketing && hit.objectID);

    if (productHits.length > 0) {
      sendEvent("view", productHits, "plp_hits_viewed");
    }
  }, [hits, marketplace.algoliaIndex, sendEvent]);

  return (
    <>
      {hits.map((hit, hitIndex) => {
        const elementId = hit.isMarketing
          ? `content-card-${hitIndex}`
          : `product-card-wrapper-${hitIndex}`;

        if (hit.isMarketing) {
          return (
            <li key={elementId}>
              <ContentCard
                {...(hit as unknown as ContentCardProps)}
                copyCodeButton={
                  <CopyCodeButton
                    ctaCodeCopied={hit.ctaCodeCopied as string}
                    ctaText={hit.ctaText as string}
                    discountCode={hit.discountCode as string}
                  />
                }
                id={elementId}
              />
            </li>
          );
        } else {
          const formattedHit = formatAlgoliaHit(
            marketplace,
            productSettings as ProductSettings,
            hit
          );

          return (
            <li key={elementId}>
              <ProductCard
                as="div"
                {...formattedHit}
                onClick={() => {
                  sendEvent(
                    "click",
                    {
                      ...formattedHit,
                      objectID: formattedHit.objectID,
                      __position: formattedHit.position,
                      __queryID: formattedHit.queryID,
                    },
                    "hit_clicked_from_plp"
                  );
                }}
                imageUrl={formattedHit.image}
                id={elementId}
                stacked={true}
              />
            </li>
          );
        }
      })}
    </>
  );
};

const renderInfiniteHits = (
  renderOptions: InfiniteHitsRenderState,
  _: unknown,
  content: GlobalContent["productListing"],
  marketplace: Marketplace,
  productSettings: ProductSettings
) => {
  const { hits, showMore, sendEvent } = renderOptions;
  const container = document.querySelector("#product-listing-grid");
  const lmContainer = document.querySelector("#load-more");

  if (!container) return;

  // Use the helper to either get an existing React root or create a new one for the main container
  const gridRoot = createUniqueRoot(container);

  // Render all cards in a single render pass.
  gridRoot.render(
    <InfiniteHits
      hits={hits}
      sendEvent={sendEvent}
      marketplace={marketplace}
      productSettings={productSettings}
    />
  );

  // Handle the "Load More" container similarly using the helper
  if (lmContainer) {
    const loadMoreRoot = createUniqueRoot(lmContainer);
    loadMoreRoot.render(<LoadMore content={content} action={showMore} />);
  }
};

const renderSortBy = (
  renderOptions: SortByRenderState,
  isFirstRender: boolean,
  sortSettings: SortSettings
) => {
  const { options, currentRefinement, refine } = renderOptions;
  const selectElement = document.querySelector("#sort-by") as HTMLSelectElement;
  if (!selectElement) return;

  if (options.length > 0) {
    options.map((option) => ({
      id: option.value,
      label: option.label,
    }));
  }

  // Handle first render
  if (isFirstRender) {
    refine(options[0].value);
    setSelectedSort("");

    // Create a placeholder option for "Sort By"
    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = `${sortSettings.sortTitleTranslation}`;
    placeholderOption.disabled = true;
    placeholderOption.hidden = true;
    selectElement.prepend(placeholderOption);
    selectElement.value = options[0].value;
  } else {
    selectElement.value = currentRefinement;
  }

  selectElement.onchange = (event: Event) => {
    const selectedValue = (event.target as HTMLSelectElement).value;
    setSelectedSort(selectedValue);
    refine(selectedValue);
  };
};

const fetchMatchedReplicas = async (
  indexRef: React.MutableRefObject<SearchIndex>,
  sortingList: SortingList[]
) => {
  try {
    const matched = await checkReplicaIndices(indexRef.current, sortingList);

    let sortOptions = [];

    if (matched && matched.length > 0) {
      sortOptions = sortingList.map((option) => {
        // Find a matching replica that includes the sorting attribute
        const matchedReplica = matched.find((replica) =>
          option.sortingAttribute
            ? replica.includes(option.sortingAttribute)
            : replica === indexRef.current.indexName
        );
        return {
          id: matchedReplica,
          label: option.sortingLabel,
        };
      });
    }

    // Return the fetched data so that the caller can decide what to do with it
    return { matched: matched || [], sortOptions };
  } catch (error) {
    console.error(`Error fetching matched replicas: ${error}`);
    throw error;
  }
};

export const InstantSearch = ({
  productSettings,
  marketplace,
  apiKey,
  appID,
  filters,
  taxonQuery,
  listingGlobalContent,
  contentCardItems,
  sortSettings,
  showSortBy,
  lastSegment,
  totalProductsLimit,
}: InstantSearchProps) => {
  const searchClient = algoliasearch(appID, apiKey);
  const indexName = useRef(marketplace.algoliaIndex);
  const isFilterBarLoaded = useStore($isFilterBarLoaded);
  const initIndexRef = useRef(searchClient.initIndex(indexName.current));
  const sortingList = useMemo(() => sortSettings.sortingList, [sortSettings]);
  const [matchedReplicas, setMatchedReplicas] = useState<string[]>([]);
  const analyticsTags = ["plp"];
  // ^ This label enables discrimination between PLP and autocomplete.
  // DO NOT REMOVE.

  if (isSpecialPageSegment(lastSegment)) {
    analyticsTags.push(lastSegment); // e.g., "bestsellers" or "new-in"
  }

  if (lastSegment === "personalized-books") {
    analyticsTags.push("all-books");
  }

  const search = useMemo(() => {
    const instance = instantsearch({
      indexName: indexName.current,
      searchClient,
      onStateChange: ({ uiState, setUiState }) => {
        const { page } = uiState[indexName.current];
        $filters.set(uiState[indexName.current].refinementList);

        setUiState(uiState);
        setPagination({
          current: page,
        });
      },
      insights: true,
    });

    return instance;
  }, [searchClient]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { matched, sortOptions } = await fetchMatchedReplicas(
          initIndexRef,
          sortingList
        );
        setMatchedReplicas(matched);
        setSortOptions(sortOptions);
      } catch (error) {
        // Optionally handle error here, e.g., setting an error state
        console.error("Error in useEffect while fetching data:", error);
      }
    };
    if (showSortBy) {
      fetchData();
    }
  }, [sortingList, showSortBy]);

  const customInfiniteHits = connectInfiniteHits(
    renderWithExtraArguments({
      renderer: renderInfiniteHits,
      extraArguments: [listingGlobalContent, marketplace, productSettings],
    })
  );

  const customSortBy = connectSortBy(
    renderWithExtraArguments({
      renderer: renderSortBy,
      extraArguments: [sortSettings],
    })
  );

  const initiateAlgoliaSearch = useCallback(() => {
    if (!search) return;
    let widgets = [];

    // Common configuration widget
    widgets.push(
      configure({
        analyticsTags,
        hitsPerPage: 23,
        query: "",
        filters: filterOptions(taxonQuery) || "",
        relevancyStrictness: 0, // Ensures Algolia doesn't filter out any results before sort is applied
        clickAnalytics: true,
        // Algolia types for don't seem to include this and other properties
        // that are available in the configure widget. 'hitsPerPage' and 'query' should
        // not trigger a ts error and works as expected.
        // https://www.algolia.com/doc/api-reference/widgets/configure/js/
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      } as any)
    );
    // Add the customInfiniteHits widget, which renders the product & content cards
    widgets.push(
      customInfiniteHits({
        transformItems: (items) => {
          const lastResults = search.helper.lastResults;
          setResultsCount(totalProductsLimit || lastResults.nbHits);
          setPagination({
            current: lastResults.page + 1, // Update current page
            total: lastResults.nbPages, // Update total pages
            totalVisibleProducts: totalProductsLimit || lastResults.hitsPerPage,
          });

          let cardsIndex = 0;
          const gap = 6;
          let currentPosition = 3; // First card is pushed after 3 product cards

          // Adjusting the order of the items to include marketing content
          return items.reduce((acc, item, index) => {
            if (!contentCardItems) {
              acc.push({ ...item, isMarketing: false });
              return acc;
            }

            if (contentCardItems.length > 0 && index === currentPosition) {
              acc.push({
                ...contentCardItems[cardsIndex],
                isMarketing: true,
              });

              if (cardsIndex < contentCardItems.length - 1) {
                cardsIndex = (cardsIndex + 1) % contentCardItems.length;
                currentPosition += gap;
              }
            }

            acc.push({ ...item, isMarketing: false });
            return acc;
          }, []);
        },
      })
    );
    // Add filters if the filter bar is loaded
    if (isFilterBarLoaded) {
      widgets = [
        ...widgets,
        ...generateFacetRefinement(filters),
        currentRefinements({
          container: "#current-refinements",
          cssClasses: {
            list: chipContainer,
            label: chipLabel,
            item: chipItemContainer,
            category: chip,
            delete: chipDelete,
          },
          transformItems: (items) => items,
        }),
      ];
    }

    // Add SortBy widget if sorting replicas exist
    // and the Prismic sorting list is not empty.
    // Prismic handles the order of the sorting list
    // options and the translation of the sorting label.
    if (matchedReplicas && matchedReplicas.length > 0) {
      widgets.push(
        customSortBy({
          items: sortSettings.sortingList.map((list) => ({
            value: list.sortingAttribute
              ? `${indexName.current}_${list.sortingAttribute}`
              : `${indexName.current}`,
            label: list.sortingLabel,
          })),
        })
      );
    }

    search.addWidgets(widgets);
    search.start();
    setSearchInstance(search);
  }, [
    isFilterBarLoaded,
    filters,
    search,
    analyticsTags,
    totalProductsLimit,
    customInfiniteHits,
    taxonQuery,
    contentCardItems,
    customSortBy,
    matchedReplicas,
    sortSettings.sortingList,
  ]);

  useEffect(() => {
    initiateAlgoliaSearch();

    return () => {
      search.dispose();
    };
  }, [isFilterBarLoaded, initiateAlgoliaSearch, search]);
};
